<template>
  <form class="form" @submit.prevent="onSubmit" novalidate>
    <component
      :is="field.component"
      v-for="field in fields"
      v-show="!field.isHidden"
      ref="formFields"
      class="form__field"
      :key="field.id"
      :value="getProps(field).value"
      :showMessageOn="showMessageOn"
      :isDisabled="isSubmitting || isDisabled || getProps(field).isDisabled"
      v-bind="getProps(field)"
      v-on="field.events || {}"
      v-data-test-id="'form__field'"
      @update:value="onValueChange({ field, value: $event })"
      @isValid="updateFormValidState"
      @focus="scrollIntoViewport"
    />
    <slot name="below-form-fields" />
    <ButtonContainer
      ref="formButton"
      v-show="isSubmitButtonVisible"
      :buttonText="submitButtonText"
      :isHidingButtonText="isHidingButtonText"
      :isSubmitting="isSubmitting || isRedirecting"
      :isDisabled="isSubmitDisabled && !isDisabledSubmitButtonInteractive"
      :isInactive="isSubmitDisabled && isDisabledSubmitButtonInteractive"
      :buttonColour="submitButtonColour"
    >
      <slot slot="above-button" name="above-button" />
    </ButtonContainer>
  </form>
</template>

<script>
import to from 'await-to-js'
import { isTranslationAvailable } from '@/helpers/localize'
import { hasKeys } from '@/helpers/object'
import { isEmpty as isEmptyString } from '@/helpers/string'
import { toNumber } from '@/helpers/number'
import { scrollSmoothlyToElement } from '@/helpers/scroll'
import { scrollIntoViewport } from '@/components/_shared/Form/form-helper'
import ButtonContainer from '@/components/_shared/Form/ButtonContainer.vue'
import { EVENT_TYPES } from '@/components/_shared/Form/Validation/FieldValidator.vue'
import { TYPES as VALIDATION_TYPES } from '@/components/_shared/Form/Validation/FieldValidation.vue'

export const VALUE_TYPES = { number: 'number' }

export default {
  name: 'Form',
  components: {
    ButtonContainer,
  },

  props: {
    fields: {
      type: Array,
      required: true,
      validator(fields) {
        return fields.every(field =>
          ['component', 'props'].every(key => {
            const isKeyPresent = !!field[key]
            if (isKeyPresent && key === 'props') return hasKeys(field[key], ['id', 'value'])
            return isKeyPresent
          })
        )
      },
    },
    handleSubmit: {
      type: Function,
      required: true,
    },
    isDisabled: {
      type: Boolean,
      default: false,
    },
    isHidingButtonText: {
      type: Boolean,
      default: false,
    },
    isSubmitBlocked: {
      type: Boolean,
      default: false,
    },
    submitButtonText: {
      type: String,
      required: true,
    },
    submitButtonColour: {
      type: String,
    },
    isSubmitButtonVisible: {
      type: Boolean,
      default: true,
    },
    isSubmitButtonDisabled: {
      type: Boolean,
      default: false,
    },
    isSubmitButtonForceEnabled: {
      type: Boolean,
      default: true,
    },
    isDisabledSubmitButtonInteractive: {
      type: Boolean,
      default: false,
    },
    isScrollingToInvalidFieldOnSubmit: {
      type: Boolean,
      default: true,
    },
    isRedirecting: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      isSubmitting: false,
      forceEnableSubmitButton: this.isSubmitButtonForceEnabled,
      showMessageOn: {
        [EVENT_TYPES.demand]: [VALIDATION_TYPES.error, VALIDATION_TYPES.warning],
        [EVENT_TYPES.blur]: [VALIDATION_TYPES.warning],
      },
      presenceValidation: {
        validator: this.isNotEmpty,
        message: this.$t('errors.required_field'),
      },
      isFormValidState: false,
    }
  },

  computed: {
    isSubmitDisabled() {
      return !(this.forceEnableSubmitButton || this.isFormValidState) || this.isSubmitButtonDisabled
    },
  },

  watch: {
    isSubmitButtonForceEnabled() {
      this.forceEnableSubmitButton = this.isSubmitButtonForceEnabled
    },
  },

  methods: {
    /* public methods */
    getFormData() {
      return this.fields.reduce((data, field) => {
        data[field.props.id] = this.convertValueFromFormToServer(field, field.props.value)
        return data
      }, {})
    },

    async validate() {
      this.$refs.formFields.forEach(({ validate }) => {
        if (validate) validate()
      })
      this.showMessageOn = {
        [EVENT_TYPES.blur]: [VALIDATION_TYPES.error, VALIDATION_TYPES.warning],
      }
      await this.$nextTick()
    },

    async setError(error) {
      this.$refs.formFields.forEach(({ setForceMessage, id }) => {
        if (setForceMessage) setForceMessage({ message: error[id], type: VALIDATION_TYPES.error })
      })
      await this.$nextTick()
      this.scrollToFirstInvalidField()
    },

    updateFormValidState() {
      const isFormValidState = this.isFormValid()
      if (this.isFormValidState !== isFormValidState) {
        this.isFormValidState = isFormValidState
        this.$emit('update:isValid', isFormValidState)
      }
    },

    isFormValid() {
      return this.fields.every(field => this.isFieldValid(field))
    },

    isFieldValid(field) {
      const { id } = field.props
      return !!this.getFormFieldById(id)?.getIsValid() || !!field.isHidden
    },

    getFormFieldById(id) {
      return this.$refs.formFields?.find(field => field.id === id)
    },

    getFormSubmitButton() {
      return this.$refs.formButton
    },

    /* private methods */
    onValueChange({ field, value }) {
      const { props } = field
      const valueConverted = this.convertValueFromFormToServer(field, value)
      if (props && props.value !== valueConverted) {
        this.$emit('update', { id: props.id, value: valueConverted })
      }
    },

    getProps(field) {
      const props = { ...field.props }
      props.value = this.convertValueFromServerToForm(field, props.value)
      if (props.isRequired) {
        const presenceValidation = { ...this.presenceValidation }
        const specificErrorMessageKey = `errors.required.${props.id}`
        if (isTranslationAvailable(specificErrorMessageKey)) {
          presenceValidation.message = this.$t(specificErrorMessageKey)
        }
        props.validations = [presenceValidation, ...(props.validations ? props.validations : [])]
      }
      return props
    },

    async onSubmit() {
      if (this.isSubmitBlocked) return

      this.$emit('click-submit')
      this.isSubmitting = true
      await this.validate()
      if (this.isFormValid()) {
        await to(this.handleSubmit(this.getFormData()))
      } else {
        this.scrollToFirstInvalidField()
      }
      this.trackFieldValidation()
      this.forceEnableSubmitButton = false
      this.isSubmitting = false
    },

    convertValueFromServerToForm(field, value) {
      const { valueType, defaultFormValue, defaultServerValue } = field
      return this.convertValue(value, valueType, defaultServerValue, defaultFormValue)
    },

    convertValueFromFormToServer(field, value) {
      const { valueType, defaultFormValue, defaultServerValue } = field
      return this.convertValue(value, valueType, defaultFormValue, defaultServerValue)
    },

    convertValue(value, valueType, defaultValue, defaultValueConverted) {
      if (
        defaultValue !== undefined &&
        defaultValueConverted !== undefined &&
        value === defaultValue
      ) {
        return defaultValueConverted
      }
      const TYPE_CONVERTERS = {
        [VALUE_TYPES.number]: value => toNumber(value),
      }
      const valueConverted = TYPE_CONVERTERS[valueType] ? TYPE_CONVERTERS[valueType](value) : value
      return defaultValueConverted !== undefined && !valueConverted // NaN, undefined, null, '', 0, false
        ? defaultValueConverted
        : valueConverted
    },

    trackFieldValidation() {
      this.fields.forEach(field => {
        if (!field.isHidden) {
          this.$emit('fieldValidation', {
            id: field.props.id,
            value: field.props.value,
            isValid: this.isFieldValid(field),
          })
        }
      })
    },

    scrollToFirstInvalidField() {
      if (!this.isScrollingToInvalidFieldOnSubmit) return

      const field = this.getFirstInvalidField()
      if (field) {
        const formField = this.getFormFieldById(field.props.id)
        if (formField?.$el) scrollSmoothlyToElement(formField.$el)
      }
    },

    async scrollIntoViewport({ currentTarget }) {
      scrollIntoViewport(currentTarget)
    },

    getFirstInvalidField() {
      return this.fields.find(field => !this.isFieldValid(field))
    },

    isNotEmpty(value) {
      return !isEmptyString(value)
    },
  },
}
</script>

<style scoped lang="scss">
.form {
  display: flex;
  justify-content: space-between;
  flex-wrap: wrap;

  &__field {
    width: 100%;
    align-self: flex-start;

    &--half {
      width: calc(50% - #{space(2)});
    }
  }
}
</style>
